import {
  IncreaseOfShareCapitalForm,
  quickSetupAbout,
  QuickSetupModel,
  QuickSetupType,
  quickSetupTypeLabels,
  quickSetupTypeTooltip,
  SubscriberForIssueModel,
} from "./increaseOfShareCapitalDomain";
import { observer } from "mobx-react";
import { Box, Grid, GridItem, VBox } from "../../components-v2/Box";
import { Dialog } from "../../components-v2/Dialog";
import { Tabs } from "../../components-v2/Tabs";
import { Button, CloseButton, IconButton, LinkButton } from "../../components-v2/Button";
import { useLocaleFormat, useTranslation } from "../../stores/RootStoreContext";
import { FullScreenLayout } from "../../components-v2/FullScreenLayout";
import { FC, Fragment, ReactElement, useEffect, useState } from "react";
import { DataTable, TableColumn } from "../../components-v2/DataTable";
import { classNames } from "../../utils/classNames";
import styles from "./QuickSetup.module.css";
import { isMobileView } from "../../utils/screen";
import { NumberFieldModel, StringFieldModel } from "../../common/commonForms";
import { MTextInput } from "../../components-v2/TextInput";
import { isEnter, isEscape } from "../../utils/keyboardEvents";
import { when } from "../../utils/functions";
import { Checkbox } from "../../components-v2/Checkbox";
import { NumberField } from "../common/commonComponents";
import { CURRENCY_NOK } from "../../utils/currency";
import { Markdown } from "../../text/Markdown";
import { Icon } from "../../components-v2/Icon";
import { MDateInput } from "../../components-v2/DateInput";
import { shareTypeLabel } from "../../common/share";
import { Alert } from "../../components-v2/Alert";
import { SelectActorsDialog } from "../../dialogs/dialogComponents";
import { DropdownMenu } from "../../components-v2/Dropdown";
import { AddNewShareholderDialog } from "./SubscribersStatus";
import { percentOf } from "../../utils/numbers";
import { compact } from "lodash";
import { EmptyState } from "../../components-v2/EmptyState";
import AutomatedSignatures from "../../assets/img/automated-signatures.svg";

export const QuickSetupDialog = observer(function QuickSetupDialog({
  form,
  model,
}: {
  form: IncreaseOfShareCapitalForm;
  model: QuickSetupModel;
}) {
  const { t } = useTranslation();

  function renderQuickSetupTab() {
    switch (model.type) {
      case QuickSetupType.CASH_PREFERENTIAL_RIGHTS:
      case QuickSetupType.CASH_PRIVATE_PLACEMENT:
        return <CashShareIssueSetup form={form} />;
    }
  }
  function renderQuickSetupCenterPanel() {
    switch (model.type) {
      case QuickSetupType.CASH_PREFERENTIAL_RIGHTS:
      case QuickSetupType.CASH_PRIVATE_PLACEMENT:
        return <ShareIssueSubscribersTable form={form} />;
    }
  }
  function renderHeaderSubtitle() {
    switch (model.type) {
      case QuickSetupType.CASH_PREFERENTIAL_RIGHTS:
        return t("decisions.increaseShareCapital.quickSetup.cashPreferentialRights.description", {
          currency: CURRENCY_NOK,
          amount: { type: "amount", value: form.totalContributionForIssue },
        });
      case QuickSetupType.CASH_PRIVATE_PLACEMENT:
        return form.shareIssue.totalShareTypePrice
          ? t("decisions.increaseShareCapital.quickSetup.cashPrivatePlacement.description", {
              currency: CURRENCY_NOK,
              subscriberAmount: { type: "amount", value: form.totalContributionForIssue },
              issuedAmount: { type: "amount", value: form.shareIssue.totalShareTypePrice },
              subscribedShares: {
                type: "list",
                value: form.shareIssue.selectedShareTypes.selected.map((s) => {
                  return t("transactions.share-transferred", {
                    shares: {
                      type: "number",
                      value: form.calculateIssuedSharesForActiveSubscribers(s),
                    },
                    shareType: { type: "uncapitilize", value: shareTypeLabel(s.sharetypeCode) },
                  });
                }),
              },
            })
          : t("decisions.increaseShareCapital.quickSetup.cashPreferentialRights.description", {
              currency: CURRENCY_NOK,
              amount: { type: "amount", value: form.shareIssue.totalShareTypePrice },
            });
    }
  }

  return (
    <Dialog fullscreen>
      <FullScreenLayout
        pageHeader={
          <Box gap={"lg"} bg borderBottom padding alignItems={"center"}>
            <CloseButton onClick={form.closeQuickSetupDialog} />
            <div>
              <div>
                {t("decisions.increaseShareCapital.quickSetup.title", {
                  setupType: { type: "uncapitilize", value: quickSetupTypeLabels[model.type] },
                })}
              </div>
              <div className={"subtle"}>{renderHeaderSubtitle()}</div>
            </div>
            <Box marginLeft={"auto"} gap={"lg"} alignItems={"center"}>
              {!model.showSetupPanel && (
                <LinkButton
                  icon={"sidebar-simple"}
                  label={t("decisions.increaseShareCapital.quickSetup.setup")}
                  onClick={model.toggleSetupPanel}
                />
              )}
              <AddSubscribersDropdown form={form} />
              <Button
                variant={"secondary"}
                label={{ key: "close" }}
                onClick={form.closeQuickSetupDialog}
              />
            </Box>
          </Box>
        }
        centerPanelContainerProps={{ className: "tw-basis-1/2" }}
        centerPanel={renderQuickSetupCenterPanel()}
        rightPanelContainerProps={{ className: "tw-basis-1/2 relative grow" }}
        rightPanel={
          model.showSetupPanel ? (
            <VBox grow overflow={"auto"} gap={false}>
              <Box gap={"lg"} paddingX paddingY={"lg"} justifyContent={"space-between"}>
                <div>
                  <h4>{t(quickSetupTypeLabels[model.type])}</h4>
                  <div className={"caption"}>{t(quickSetupTypeTooltip[model.type])}</div>
                </div>
                <Box alignItems={"start"}>
                  <IconButton
                    icon={"x"}
                    tooltip={t("close")}
                    color="gray"
                    onClick={model.toggleSetupPanel}
                  />
                </Box>
              </Box>
              <Box display={"block"} borderBottom>
                <Tabs tabs={model.tabs} />
              </Box>
              <VBox paddingX paddingTop>
                {model.showQuickSetupTab ? (
                  <>
                    {renderQuickSetupTab()}
                    <VBox paddingX paddingY={"lg"}>
                      <VBox
                        textAlign={"center"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        gap={"lg"}
                        paddingY={"lg"}
                      >
                        <Icon name={"lightbulb"} size={"lg"} color={"subtle"} />
                        <VBox gap={false}>
                          <div className={"subtle"}>
                            {t("decisions.increaseShareCapital.quickSetup.footer1")}
                          </div>
                          <div className={"subtle"}>
                            {t("decisions.increaseShareCapital.quickSetup.footer2")}
                            <LinkButton
                              label={t("decisions.increaseShareCapital.quickSetup.footer2.link")}
                              onClick={form.closeQuickSetupDialog}
                            />
                          </div>
                        </VBox>
                      </VBox>
                    </VBox>
                  </>
                ) : (
                  <VBox gap={"lg"} paddingX paddingTop>
                    <Markdown value={t(quickSetupAbout[model.type])} />
                  </VBox>
                )}
              </VBox>
            </VBox>
          ) : undefined
        }
      />
      {form.shareIssue.addShareholdersFromActorsModel && (
        <SelectActorsDialog model={form.shareIssue.addShareholdersFromActorsModel} />
      )}
      {form.shareIssue.newSubscriberForIssue && form.shareIssue.searchCompany && (
        <AddNewShareholderDialog
          model={form.shareIssue.newSubscriberForIssue}
          searchCompany={form.shareIssue.searchCompany}
          onSubmit={form.shareIssue.submitNewShareholderSubscriber}
          onClose={form.shareIssue.closeAddSubscriberDialog}
        />
      )}
    </Dialog>
  );
});

export const CashShareIssueSetup: FC<{
  form: IncreaseOfShareCapitalForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocaleFormat();

  return (
    <VBox gap={"lg"} paddingX paddingTop>
      <VBox>
        <Grid alignItems={"start"}>
          <GridItem xs={form.shareIssue.selectedShareTypes.isNoneSelected ? 12 : 3}>
            {t("decisions.increaseShareCapital.quickSetup.issue")}
          </GridItem>
          {!form.shareIssue.selectedShareTypes.isNoneSelected && (
            <>
              <GridItem xs={3}>
                {t("decisions.increaseShareCapital.quickSetup.issue.numberOfShares")}
              </GridItem>
              <GridItem xs={3}>
                {t("decisions.increaseShareCapital.quickSetup.issue.price")}
              </GridItem>
              <GridItem xs={3} textAlign={"right"}>
                {t("decisions.increaseShareCapital.quickSetup.issue.sum", {
                  currency: CURRENCY_NOK,
                })}
              </GridItem>
            </>
          )}
          {form.shareIssue.selectedShareTypes.options.map((option) => {
            return (
              <Fragment key={option.sharetypeId}>
                <GridItem
                  xs={form.shareIssue.selectedShareTypes.isSelected(option) ? 3 : 12}
                  marginY={"sm"}
                >
                  <Checkbox
                    key={form.shareIssue.selectedShareTypes.getId(option)}
                    label={form.shareIssue.selectedShareTypes.getLabel(option)}
                    onChange={() => form.shareIssue.toggleSelectedShareType(option)}
                    checked={form.shareIssue.selectedShareTypes.isSelected(option)}
                    disabled={form.isProjectLocked}
                  />
                </GridItem>
                {form.shareIssue.selectedShareTypes.isSelected(option) && (
                  <>
                    <GridItem xs={3}>
                      <NumberField
                        field={option.model.fixed.numberOfShares}
                        onBlur={form.shareIssue.setSubscribersForIssue}
                        disabled={form.isProjectLocked}
                      />
                    </GridItem>
                    <GridItem xs={3}>
                      <NumberField
                        prefix={CURRENCY_NOK}
                        field={option.model.sharePrice}
                        onBlur={form.shareIssue.setSubscribersForIssue}
                        disabled={form.isProjectLocked}
                      />
                    </GridItem>
                    <GridItem xs={3} marginY={"sm"} textAlign={"right"}>
                      {formatNumber(option.model.maxTotalPrice)}
                    </GridItem>
                  </>
                )}
              </Fragment>
            );
          })}
          <GridItem xs={9} marginY={"sm"}>
            {t("decisions.increaseShareCapital.quickSetup.issue.total", { currency: CURRENCY_NOK })}
          </GridItem>
          <GridItem xs={3} marginY={"sm"} textAlign={"right"} fontWeight={"bold"}>
            {formatNumber(form.shareIssue.totalShareTypePrice)}
          </GridItem>
        </Grid>
      </VBox>
      {form.isPreferentialRight && (
        <Alert
          message={t(
            "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.info.subscribersCalculated",
          )}
          type={"info"}
        />
      )}
      <VBox gap={"lg"} paddingTop={"sm"}>
        <div>{t("options")}</div>
        {form.isPreferentialRight && (
          <>
            <Checkbox
              label={t(
                "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.transferableSubscriptionRights",
              )}
              onChange={() => {
                form.shareIssue.preferentialRightTransferable.toggle();
              }}
              checked={form.shareIssue.preferentialRightTransferable.value === true}
              disabled={form.isProjectLocked}
            />
            <Checkbox
              label={t(
                "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.oversubscription",
              )}
              onChange={() => {
                form.shareIssue.preferentialRightOversubscribe.toggle();
              }}
              checked={form.shareIssue.preferentialRightOversubscribe.value === true}
              disabled={form.isProjectLocked}
            />
            <Checkbox
              label={t(
                "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.boardRightsToSellSubscription",
              )}
              onChange={() => {
                form.shareIssue.preferentialBoardRightsToSellSubscription.toggle();
              }}
              checked={form.shareIssue.preferentialBoardRightsToSellSubscription.value === true}
              disabled={form.isProjectLocked}
            />
          </>
        )}
        <Checkbox
          label={t(
            "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.subscriptionForm",
          )}
          onChange={(e) => {
            form.setIsSubscriptionForm(e.target.checked);
          }}
          checked={form.isSubscriptionForm.value === true}
          disabled={form.isProjectLocked}
        />
        {form.isSubscriptionForm.value === true && (
          <Box>
            <MDateInput
              model={form.periodForSubscription.from}
              label={t(
                "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.subscriptionForm.startDate",
              )}
              disabled={form.isProjectLocked}
            />
            <MDateInput
              model={form.periodForSubscription.to}
              label={t(
                "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.subscriptionForm.endDate",
              )}
              disabled={form.isProjectLocked}
            />
          </Box>
        )}
        {form.containsCash && form.isExternalSubscribers && (
          <Checkbox
            label={t(
              "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.variableSharePrice",
            )}
            disabled
          />
        )}
        <MDateInput
          model={form.deadlineForSubscription}
          label={t(
            "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.deadlineForSubscription",
          )}
          disabled={form.isProjectLocked}
        />
      </VBox>
    </VBox>
  );
});

export const ShareIssueSubscribersTable: FC<{ form: IncreaseOfShareCapitalForm }> = observer(
  ({ form }) => {
    const { t } = useTranslation();
    const { formatNumber, formatPercent } = useLocaleFormat();
    const [tableContainerRef, setTableContainerRef] = useState<HTMLDivElement | null>(null);

    const selectedShareTypeColumns = form.shareIssue.selectedShareTypes.selected.flatMap<
      TableColumn<SubscriberForIssueModel>
    >((shareType) => {
      return compact([
        form.isPreferentialRight
          ? undefined
          : {
              name: `${shareType.sharetypeCode}-price`,
              label:
                form.shareIssue.selectedShareTypes.selected.length > 1
                  ? t(
                      "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.table.totalPricePershareType",
                      {
                        shareType: {
                          type: "uncapitilize",
                          value: shareTypeLabel(shareType.sharetypeCode),
                        },
                      },
                    )
                  : t(
                      "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.table.totalPrice",
                    ),
              width: "20%",
              align: "right",
              getSortValue: (s) => s.getTotalPriceForShareTypeId(shareType.sharetypeId),
              getValue: (s) => {
                const subscriptionForShareType = s.getSubscriptionForShareType(
                  shareType.sharetypeId,
                  shareType.sharetypeCode,
                  shareType.model.sharePrice.toNumberOrZero(),
                );
                return (
                  <OnclickTextField
                    model={subscriptionForShareType.totalSubscriptionPrice}
                    onBlur={() => subscriptionForShareType.totalSubscriptionPrice.commit()}
                    align={"right"}
                    disabled={form.isPreferentialRight || form.isProjectLocked}
                  />
                );
              },
            },
        {
          name: `${shareType.sharetypeCode}-numberOfShares`,
          label:
            form.shareIssue.selectedShareTypes.selected.length > 1
              ? t(
                  "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.table.newShares",
                  {
                    shareType: {
                      type: "uncapitilize",
                      value: shareTypeLabel(shareType.sharetypeCode),
                    },
                  },
                )
              : t("decisions.increaseShareCapitalAgainstCash.numberOfShares"),
          width: "20%",
          align: "right",
          getSortValue: (s) =>
            s
              .getSubscriptionForShareType(
                shareType.sharetypeId,
                shareType.sharetypeCode,
                shareType.model.sharePrice.toNumberOrZero(),
              )
              .numberOfShares.toNumberOrZero(),
          getValue: (s) => {
            const subscriptionForShareType = s.getSubscriptionForShareType(
              shareType.sharetypeId,
              shareType.sharetypeCode,
              shareType.model.sharePrice.toNumberOrZero(),
            );
            return (
              <OnclickTextField
                model={subscriptionForShareType.numberOfShares}
                onBlur={() => subscriptionForShareType.numberOfShares.commit()}
                align={"right"}
                disabled={form.isPreferentialRight || form.isProjectLocked}
              />
            );
          },
        },
      ]);
    });

    const tableColumnTotalPrice: TableColumn<SubscriberForIssueModel>[] =
      form.isPreferentialRight || form.shareIssue.selectedShareTypes.selected.length > 1
        ? [
            {
              name: `totalPrice`,
              label: t(
                "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.table.totalPrice",
              ),
              width: "20%",
              align: "right",
              getSortValue: (s) =>
                s.getTotalPriceForShareTypes(form.shareIssue.selectedShareTypeIds),
              getValue: (s) => {
                return (
                  <TableCell
                    align={"right"}
                    value={formatNumber(
                      s.getTotalPriceForShareTypes(form.shareIssue.selectedShareTypeIds),
                    )}
                    disabled
                  />
                );
              },
            },
          ]
        : [];

    const tableColumns: Array<TableColumn<SubscriberForIssueModel>> = [
      {
        name: "name",
        label: t("name"),
        width: "30%",
        align: "left",
        getSortValue: (s) => s.nameField.validatedValue,
        getValue: (s) => (
          <TableCell
            align={"left"}
            value={
              <Box alignItems={"center"} gap={"md"}>
                <IconButton
                  compact
                  icon={"trash"}
                  tooltip={t("delete")}
                  onClick={() => form.shareIssue.deleteSubscriber(s)}
                  disabled={form.isPreferentialRight}
                />
                {s.nameField.validatedValue}
              </Box>
            }
            disabled={form.isPreferentialRight}
          />
        ),
      },
      ...tableColumnTotalPrice,
      ...selectedShareTypeColumns,
      {
        name: "currentTotal",
        label: t(
          "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.table.currentOwnership",
        ),
        width: "20%",
        align: "right",
        getSortValue: (s) => s.currentTotalNumberOfShares,
        getValue: (s) => {
          return (
            <TableCell
              align={"right"}
              value={`${formatNumber(s.currentTotalNumberOfShares)} (${formatPercent(
                percentOf(s.currentTotalNumberOfShares, form.shareIssue.currentNumberOfShares),
              )})`}
              disabled
            />
          );
        },
      },
      {
        name: "newTotal",
        label: t(
          "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.table.newOwnership",
        ),
        width: "20%",
        align: "right",
        getSortValue: (s) => s.newTotalNumberOfShares,
        getValue: (s) => {
          return (
            <TableCell
              align={"right"}
              value={`${formatNumber(s.newTotalNumberOfShares(form.shareIssue.selectedShareTypeIds))} (${formatPercent(
                percentOf(
                  s.newTotalNumberOfShares(form.shareIssue.selectedShareTypeIds),
                  form.shareIssue.newNumberOfShares,
                ),
              )})`}
              disabled
            />
          );
        },
      },
    ];

    useEffect(() => {
      const el = tableContainerRef;
      return () => {
        // Reset scroll on unmount
        el?.scrollTo(0, 0);
      };
    }, [tableContainerRef]);

    return form.subscribersForIssueCandidates.length === 0 ? (
      <VBox grow={"golden"} justifyContent={"center"}>
        <EmptyState
          description={t(
            "decisions.increaseShareCapital.subscribersStatus.emptySubscribers.header",
          )}
          illustration={<img src={AutomatedSignatures} alt="dCompany" />}
          actions={<AddSubscribersDropdown form={form} variant={"button"} />}
        />
      </VBox>
    ) : (
      <VBox
        overflow={"hidden"}
        className={classNames(styles.stickyHeaderRow, !isMobileView() && styles.stickyHeaderCol)}
      >
        <Box overflow={"auto"} ref={setTableContainerRef}>
          <DataTable
            containerClassName={"grow"}
            variant="EDITABLE"
            tableColumns={tableColumns}
            tableData={form.subscribersForIssueCandidates}
            getId={(row) => row.subscriberid}
            headerLinkClassName={"tw-px-4"}
          />
        </Box>
      </VBox>
    );
  },
);

const OnclickTextField: FC<{
  model: StringFieldModel | NumberFieldModel;
  onBlur?: () => void;
  disabled?: boolean;
  align?: "left" | "center" | "right";
  autofocus?: boolean;
}> = observer(({ model, onBlur, disabled, align, autofocus }) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocaleFormat();
  const [enabled, setEnabled] = useState(autofocus ?? false);
  return enabled && !disabled ? (
    <Box className={"tw-min-w-40 tw-w-full"}>
      <MTextInput
        model={model}
        onBlur={() => {
          onBlur?.();
          if (model.isValid) setEnabled(false);
        }}
        autofocus
        onKeyDown={(e) => {
          if (isEnter(e) || isEscape(e)) {
            onBlur?.();
            if (model.isValid) setEnabled(false);
          }
        }}
        align={align}
      />
    </Box>
  ) : (
    <Box
      htmlProps={{ tabIndex: disabled ? undefined : 0, onFocus: () => setEnabled(true) }}
      className={classNames(
        "tw-leading-tight",
        "tw-py-3",
        "tw-px-4",
        !disabled && "tw-cursor-text",
        "tw-min-w-40",
        "tw-w-full",
        "tw-truncate",
        "tw-border-b-2",
        !model.isValid ? "border-error" : "border-transparent",
      )}
      textAlign={align}
      whitespace={"nowrap"}
      justifyContent={align === "right" ? "end" : undefined}
      bg={disabled ? "alt1" : "default"}
      tooltip={model.isValid ? undefined : when(model.error, t)}
    >
      {model instanceof StringFieldModel ? (
        model.validatedValue
      ) : model.parsedValue !== undefined ? (
        formatNumber(model.parsedValue)
      ) : (
        <>&nbsp;</>
      )}
    </Box>
  );
});

const TableCell: FC<{
  value: string | ReactElement;
  align?: "left" | "center" | "right";
  disabled?: boolean;
}> = observer(({ value, align, disabled }) => {
  return (
    <Box
      className={classNames(
        "tw-leading-tight",
        "tw-py-3",
        "tw-px-4",
        "tw-min-w-40",
        "tw-w-full",
        "tw-truncate",
        "tw-border-b-2",
        "border-transparent",
      )}
      textAlign={align}
      whitespace={"nowrap"}
      justifyContent={align === "right" ? "end" : undefined}
      bg={disabled ? "alt1" : "default"}
    >
      {value}
    </Box>
  );
});

const AddSubscribersDropdown: FC<{
  form: IncreaseOfShareCapitalForm;
  variant?: "button" | "link";
}> = observer(({ form, variant = "link" }) => {
  const { t } = useTranslation();
  return (
    <DropdownMenu
      button={
        variant === "link"
          ? {
              type: "link",
              icon: "plus",
              label: t("decisions.increaseShareCapital.subscribersStatus.addSubscribers"),
              disabled: form.isProjectLocked || form.isPreferentialRight,
            }
          : {
              type: "button",
              variant: "secondary",
              icon: "plus",
              label: t("decisions.increaseShareCapital.subscribersStatus.addSubscribers"),
              disabled: form.isProjectLocked || form.isPreferentialRight,
            }
      }
      items={[
        {
          type: "item",
          label: t("subscriber.addBuyer.existing"),
          onSelect: () => form.shareIssue.openAddNewShareholderFromPortfolioDialog(),
        },
        {
          type: "item",
          label: t("subscriber.addBuyer.newPerson"),
          onSelect: () => form.shareIssue.openAddNewShareholderDialog(false),
        },
        {
          type: "item",
          label: t("subscriber.addBuyer.newCompany"),
          onSelect: () => form.shareIssue.openAddNewShareholderDialog(true),
        },
      ]}
    />
  );
});
